import { Extension, findParentNodeClosestToPos, isNodeSelection } from "@tiptap/core";
import { Plugin, Selection, TextSelection } from "@tiptap/pm/state";
import { CellSelection, TableMap } from "@tiptap/pm/tables";

import { trackedHintType } from "@/components/suggestions/types";
import { arrowToCollapsed } from "@/components/tiptap/collapse/arrowNavigation";
import {
  collapsedBlockInDirection,
  collapsedRunInfo,
  expandBlock,
  expandRunHead,
  isCollapsedRunSelection,
} from "@/components/tiptap/collapse/blockCollapse";
import { collapsedSectionAbove } from "@/components/tiptap/collapse/buildCollapseRunDecorations";
import {
  closestCollapsedSection,
  collapsedSectionInDirection,
  expandSection,
} from "@/components/tiptap/collapse/sectionsCollapse";
import {
  handleIndentTab,
  handleListTab,
  isIndented,
  removeIndentAtBlockStart,
} from "@/components/tiptap/keyboard-handler/blockIndent";
import { moveToMath, nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { deleteSelection } from "@/components/tiptap/utils/deleteSelection";
import { duplicateEditorSelection } from "@/components/tiptap/utils/duplicateTiptapLine";
import { isCellSelection } from "@/components/tiptap/utils/isCellSelection";
import { isEveryCellSelected } from "@/components/tiptap/utils/isEveryCellSelected";
import { acceptPhantomHint, acceptTableRowHints } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isReplayableComposedChar, replayKeydown } from "@/utils/AndroidSymbolKeydownReplay";
import { getIsAndroid } from "@/utils/getIsAndroid";
import { toProsemirrorHotkey } from "@/utils/hotkeys";
import { isModifierKey } from "@/utils/keyMatcher";

import type { DocumentModel } from "@/models/DocumentModel";
import type { CommandProps, Editor } from "@tiptap/core";
import type { Node as ProsemirrorNode, ResolvedPos } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

type KeyboardHandlerExtensionOptions = {
  doc: DocumentModel;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    keyboardHandler: {
      prependParagraph: (options?: { forceFocus?: boolean; pos?: number }) => ReturnType;
      appendParagraph: (options?: { forceFocus?: boolean; pos?: number }) => ReturnType;
      splitBlockOrListItem: () => ReturnType;
      deleteEmptyParagraphBefore: () => ReturnType;
      selectDocument: () => ReturnType;
    };
  }
}

const COLUMN_CREATE_DEBOUNCE_MS = 300;

export const KeyboardHandler = Extension.create<
  KeyboardHandlerExtensionOptions,
  {
    lastTypedKeyAt: number | null;
  }
>({
  name: "keyboardHandler",

  addStorage() {
    return { lastTypedKeyAt: null };
  },

  addCommands() {
    return {
      selectDocument: () => (props) => {
        return handleAllDocSelection(props, this.options.doc);
      },
      prependParagraph:
        (options) =>
        ({ state, commands }) => {
          const prevBlockPosition = state.selection.$from.before(1);
          commands.insertContentAt(options?.pos ?? prevBlockPosition, "<p></p>");
          if (options?.forceFocus) {
            commands.focus();
          }
          return true;
        },
      appendParagraph:
        (options) =>
        ({ state, commands }) => {
          const nextBlockPosition = state.selection.$to.after(1);
          commands.insertContentAt(options?.pos ?? nextBlockPosition, "<p></p>");
          if (options?.forceFocus) {
            commands.focus();
          }
          return true;
        },
      splitBlockOrListItem:
        () =>
        ({ state, commands }) => {
          const listItemNode = findParentNodeClosestToPos(
            state.selection.$from,
            (n) => n.type.name === "listItem" || n.type.name === "taskItem",
          );

          if (listItemNode) {
            return commands.splitListItem(listItemNode.node.type);
          }

          return commands.splitBlock();
        },
      deleteEmptyParagraphBefore:
        () =>
        ({ tr, dispatch }) => {
          const { $from } = tr.selection;
          const n = $from.nodeBefore;
          if (!n) return false;
          if (n.isTextblock && n.content.size === 0 && n.type.name !== "documentTitle") {
            if (dispatch) {
              tr.delete($from.pos - n.nodeSize, $from.pos);
            }
            return true;
          }

          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { selection } = this.editor.state;
        const collapsedSection = closestCollapsedSection(selection);

        if (collapsedSection) {
          return expandSection(this.editor, collapsedSection.pos);
        }

        if (isCollapsedRunSelection(this.editor.view, selection)) {
          return expandBlock(this.editor, selection.from);
        }

        if (this.options.doc.editor.suggestions.visible) {
          // Normally the document-level suggestions handler has already picked and
          // stopped the keydown, so this only swallows Enter. On Chrome Android the
          // Enter arrives inside the IME composition and ProseMirror re-synthesizes
          // it straight into this keymap — pick here so it isn't lost.
          this.options.doc.editor.suggestions.selectActive();
          return true;
        }
        const { isHeading, isTableCellLike } = this.options.doc.editor.editorState;
        if (isHeading) {
          this.editor.commands.unsetFontSize();
          return false;
        }
        if (isTableCellLike) {
          if (this.editor.commands.goToNextCellVertical(1)) {
            return true;
          }

          return this.editor.commands.appendParagraph();
        }

        if (this.options.doc.onSubmit) {
          this.options.doc.onSubmit();
          return true;
        }

        return false;
      },
      Escape: () => {
        if (this.options.doc.editor.suggestions.visible) {
          return true;
        }
        return false;
      },
      "Shift-Enter": () => {
        const { selection } = this.editor.state;
        if (isCollapsedRunSelection(this.editor.view, selection)) {
          return expandBlock(this.editor, selection.from);
        }

        const { isCodeBlock, isTableCellLike, isImage } = this.options.doc.editor.editorState;
        if (isCodeBlock) {
          return this.editor.commands.newlineInCode();
        }

        if (isTableCellLike) {
          return this.editor.chain().addRowAfter().goToNextCellVertical(1).run();
        }

        if (isImage) {
          return this.editor.commands.appendParagraph();
        }

        return false;
      },
      "Alt-Enter": () => {
        const { isTableCellLike } = this.options.doc.editor.editorState;

        if (isTableCellLike) {
          return this.editor.commands.setHardBreak();
        }

        return this.editor.commands.appendParagraph();
      },
      "Mod-Enter": () => {
        if (this.options.doc.onSubmit) {
          return this.editor.commands.setHardBreak();
        }

        return this.editor.commands.appendParagraph();
      },
      "Mod-Shift-Enter": () => {
        return this.editor.commands.prependParagraph();
      },
      "Shift-Space": () => {
        const { editor } = this.options.doc;

        if (editor.editorState.isTableCellLike && !editor.suggestions.activeSuggestion) {
          const { lastTypedKeyAt } = this.storage;
          const canAddColumn =
            !lastTypedKeyAt || Date.now() - lastTypedKeyAt > COLUMN_CREATE_DEBOUNCE_MS;

          if (canAddColumn) {
            return this.editor.chain().addColumnAfter().goToNextCell().run();
          }
        }

        return false;
      },
      Backspace: () => {
        const { selection } = this.editor.state;

        if (isCollapsedRunSelection(this.editor.view, selection)) {
          return expandBlock(this.editor, selection.from);
        }

        if (isCellSelection(selection)) {
          if (isEveryCellSelected(selection)) {
            return false;
          }

          return handleCellSelectionBackspacePress(selection, this.editor);
        }

        return handleBackspacePress(this.options.doc, this.editor);
      },
      Delete: () => {
        return handleDeletePress(this.options.doc, this.editor);
      },
      ArrowLeft: () => {
        if (arrowToCollapsed(this.editor.view, "ArrowLeft")) {
          return true;
        }

        return handleArrowLeftRightPress(
          "left",
          this.options.doc,
          this.editor,
          this.editor.state.selection,
        );
      },
      ArrowRight: () => {
        if (arrowToCollapsed(this.editor.view, "ArrowRight")) {
          return true;
        }

        return handleArrowLeftRightPress(
          "right",
          this.options.doc,
          this.editor,
          this.editor.state.selection,
        );
      },
      "Alt-ArrowLeft": () => {
        return handleAltArrowPress("left", this.options.doc, this.editor);
      },
      "Alt-ArrowRight": () => {
        return handleAltArrowPress("right", this.options.doc, this.editor);
      },
      ArrowUp: () => {
        if (arrowToCollapsed(this.editor.view, "ArrowUp")) {
          return true;
        }

        return handleArrowUpDownPress("up", this.options.doc, this.editor);
      },
      ArrowDown: () => {
        if (arrowToCollapsed(this.editor.view, "ArrowDown")) {
          return true;
        }

        const firstNode = this.editor.state.doc.firstChild;
        const isInTitle =
          firstNode?.type.name === "documentTitle" &&
          this.editor.state.selection.from < firstNode.nodeSize;

        const result = handleArrowUpDownPress("down", this.options.doc, this.editor);

        // When jumping from the title, release lockLeft so subsequent jumps
        // use coordinates based on where we landed, not the title position.
        if (isInTitle) {
          this.options.doc.cursor.releaseLockLeft();
        }

        return result;
      },
      Tab: () => {
        const { doc } = this.options;
        if (doc.editor.suggestions.visible) {
          return true;
        }

        if (cursorIsActive(doc.cursor)) {
          return false;
        }

        // Cursor in a row-header formula cell: fill every empty cell of that row at once.
        if (doc.editor.hints.hasTableRowHints) {
          acceptTableRowHints(doc);
          return true;
        }

        const { phantomHintResult, phantomHint } = doc.editor.hints;
        if (phantomHintResult) {
          const inlineNode = doc.create(InlineNodeModel);
          doc.editor.tiptap?.commands.insertInlineExpression(inlineNode);
          acceptPhantomHint(
            doc,
            phantomHintResult.children,
            trackedHintType(phantomHint),
            inlineNode,
          );
          return true;
        }

        // As typing does, Tab into a collapsed block expands it first, rather than indenting it unseen.
        const run = collapsedRunInfo(this.editor.view, this.editor.state.selection);

        if (run) {
          expandRunHead(this.editor, run.first);
        }

        return handleListTab(this.editor, "indent") || handleIndentTab(this.editor, "indent");
      },
      "Shift-Tab": () => {
        const { doc } = this.options;
        if (doc.editor.suggestions.visible) {
          return true;
        }

        if (cursorIsActive(doc.cursor)) {
          return false;
        }

        // As with Tab, outdenting a collapsed run expands it first, if any of its blocks has an
        // indent to remove; one with nothing to outdent stays collapsed.
        const run = collapsedRunInfo(this.editor.view, this.editor.state.selection);
        let hasIndent = false;

        if (run) {
          this.editor.state.doc.nodesBetween(run.first, run.runEnd, (node) => {
            hasIndent ||= isIndented(node);
            return false;
          });
        }

        if (run && hasIndent) {
          expandRunHead(this.editor, run.first);
        }

        return handleListTab(this.editor, "outdent") || handleIndentTab(this.editor, "outdent");
      },
      [toProsemirrorHotkey("duplicate-line")]: () => {
        return handleDuplicate(this.options.doc, this.editor);
      },
      /* alignment shortcuts */
      [toProsemirrorHotkey("align-left")]: () => this.editor.commands.setAlign("left"),
      [toProsemirrorHotkey("align-center")]: () => this.editor.commands.setAlign("center"),
      [toProsemirrorHotkey("align-right")]: () => this.editor.commands.setAlign("right"),
    };
  },

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin({
        props: {
          handleKeyDown: (_view, event) => {
            if (doc.editor.editorState.isTableCellLike && !isModifierKey(event)) {
              this.storage.lastTypedKeyAt = Date.now();
            }
          },
          handleTextInput: handleAndroidComposedChar,
          handleDOMEvents: {
            beforeinput: handleAndroidDeleteBackward,
          },
        },
      }),
    ];
  },
});

/**
 * Android soft keyboards delete through `beforeinput` (`deleteContentBackward`) instead of a
 * Backspace keydown. At the start of a block Chromium can't perform that deletion natively
 * across our line-wrapper node views, and prosemirror-view's Android fallback then blurs and
 * refocuses the editor before replaying Backspace through the keymap — which closes and
 * reopens the keyboard. Replay Backspace right away and cancel the native deletion instead,
 * so the DOM only changes through a ProseMirror transaction and focus never moves.
 */
function handleAndroidDeleteBackward(view: EditorView, event: Event): boolean {
  const { inputType, isComposing } = event as InputEvent;

  if (!getIsAndroid() || inputType !== "deleteContentBackward") {
    return false;
  }

  const { selection } = view.state;

  if (
    view.composing ||
    isComposing ||
    !(selection instanceof TextSelection) ||
    selection.$cursor?.parentOffset !== 0
  ) {
    return false;
  }

  event.preventDefault();
  view.someProp("handleKeyDown", (handler) =>
    handler(
      view,
      new KeyboardEvent("keydown", { key: "Backspace", code: "Backspace", keyCode: 8 }),
    ),
  );

  return true;
}

/**
 * Replays a composed char's keydown (see `isReplayableComposedChar`) before ProseMirror inserts
 * it, so the text converts to math as on desktop and iOS.
 */
function handleAndroidComposedChar(
  view: EditorView,
  from: number,
  to: number,
  text: string,
): boolean {
  if (!getIsAndroid() || !view.composing || from !== to) {
    return false;
  }

  const charBefore = view.state.doc.resolve(from).nodeBefore?.text?.slice(-1) ?? null;
  if (!isReplayableComposedChar(text, charBefore)) {
    return false;
  }

  return replayKeydown(view.dom, text);
}

function handleDuplicate(doc: DocumentModel, tiptap: Editor) {
  if (doc.editor.suggestions.visible) {
    return true;
  }

  return duplicateEditorSelection(doc, tiptap);
}

function handleAllDocSelection({ view, commands }: CommandProps, doc: DocumentModel) {
  const { selection, doc: pmDoc } = view.state;
  const { firstChild } = pmDoc;

  const title = firstChild?.type.name === "documentTitle" ? firstChild : null;
  const titleEnd = title?.nodeSize ?? 0;

  const bodyFromShift = ["table", "image", "fileAttachment"].includes(
    pmDoc.nodeAt(titleEnd)?.type.name ?? "",
  )
    ? 0
    : 1;
  const bodyFrom = titleEnd + bodyFromShift;
  const bodyEnd = Selection.atEnd(pmDoc).to;

  const selectBody = (includeTitle = false) => {
    const from = includeTitle && firstChild?.textContent ? 1 : bodyFrom;
    const firstBodyNode = pmDoc.nodeAt(from);

    if (nodeIsExpression(firstBodyNode)) {
      const wrapperNode = doc.nodes.get(String(firstBodyNode.attrs.nodeId ?? ""));

      if (wrapperNode?.is(WrapperNodeModel)) {
        doc.selection.selectWrapperNode(wrapperNode, "forward");
      }
    }

    return commands.setTextSelection({ from, to: bodyEnd });
  };

  if (title) {
    const titleSelected = selection.from === 1 && selection.to === titleEnd;

    if (titleSelected) {
      return selectBody(true);
    }

    const cursorInTitle = selection.head < titleEnd;

    if (cursorInTitle) {
      return commands.setTextSelection({ from: 0, to: titleEnd });
    }
  }

  const table = findParentNodeClosestToPos(selection.$head, (node) => node.type.name === "table");

  if (table) {
    if (isCellSelection(selection)) {
      const map = TableMap.get(table.node);

      if (map.map.length === selection.ranges.length) {
        return selectBody();
      }

      const start = table.pos + 1;
      const lastCell = start + map.map[map.width * map.height - 1];

      return commands.command(({ tr, dispatch }) => {
        if (dispatch) {
          tr.setSelection(CellSelection.create(tr.doc, start + 1, lastCell));
        }
        return true;
      });
    }

    const cell = findParentNodeClosestToPos(
      selection.$head,
      (node) => node.type.name === "tableCell" || node.type.name === "tableHeader",
    );

    if (cell) {
      return commands.setNodeSelection(cell.pos);
    }
  }

  const bodySelected =
    !isNodeSelection(selection) && selection.from === bodyFrom && selection.to === bodyEnd;

  return selectBody(bodySelected);
}

function handleCellSelectionBackspacePress(selection: CellSelection, editor: Editor) {
  if (selection.isColSelection()) {
    return editor.commands.deleteColumn();
  }

  if (selection.isRowSelection()) {
    return editor.commands.deleteRow();
  }

  return false;
}

function handleBackspacePress(doc: DocumentModel, editor: Editor) {
  const { selection } = editor.view.state;
  const { from, to, $head } = selection;

  if (from === to) {
    if ($head.parentOffset === 0) {
      if (removeIndentAtBlockStart(editor)) {
        return true;
      }

      const collapsedBlockPos = collapsedBlockInDirection(editor.view, "up");

      if (collapsedBlockPos !== null) {
        return expandBlock(editor, collapsedBlockPos, false);
      }

      const collapsedSectionPos = collapsedSectionAbove(editor.view);

      if (collapsedSectionPos !== null) {
        return expandSection(editor, collapsedSectionPos, { focus: false });
      }

      // Check if cursor is at the start of a paragraph with non-left alignment
      if ($head.parent.type.name === "paragraph") {
        const { alignment } = $head.parent.attrs;
        if (alignment === "right") {
          editor.commands.setAlign("center");
          return true;
        }

        if (alignment === "center") {
          editor.commands.setAlign("left");
          return true;
        }
      }
    }

    if (nodeIsExpression($head.nodeBefore)) {
      moveToMath(doc, $head.nodeBefore, { placeCursorToEnd: true });
      return true;
    }

    if (wouldCrossChangeRequestBoundary($head, "left")) {
      moveToSiblingSelection("left", from, editor);
      return true;
    }

    /**
     * Chrome has an issue where native {Backspace} press near a
     * contenteditable="false" inline atom inserts a rogue <BR> instead of
     * deleting the preceding character. Explicitly dispatch the deletion to
     * prevent the browser from handling it at all.
     *
     * https://github.com/ProseMirror/prosemirror/issues/831
     */
    if (nodeIsExpression($head.nodeAfter) && $head.parentOffset > 0) {
      const { tr } = editor.state;
      editor.view.dispatch(tr.delete(from - 1, from));
      return true;
    }
  }

  return deleteSelection(doc, editor.view);
}

function handleDeletePress(doc: DocumentModel, editor: Editor) {
  const { selection } = editor.view.state;
  const { from, to, $head } = selection;

  if (from === to) {
    if (editor.view.endOfTextblock("right")) {
      const collapsedBlockPos = collapsedBlockInDirection(editor.view, "down");

      if (collapsedBlockPos !== null) {
        return expandBlock(editor, collapsedBlockPos, false);
      }

      const collapsedSectionPos = collapsedSectionInDirection(editor.view, "down");

      if (collapsedSectionPos !== null) {
        return expandSection(editor, collapsedSectionPos, { focus: false });
      }
    }

    if (nodeIsExpression($head.nodeAfter)) {
      moveToMath(doc, $head.nodeAfter);
      return true;
    }

    if (wouldCrossChangeRequestBoundary($head, "right")) {
      moveToSiblingSelection("right", from, editor);
      return true;
    }

    // Symmetric to the Backspace fix above
    if (nodeIsExpression($head.nodeBefore) && $head.parentOffset < $head.parent.content.size) {
      const { tr } = editor.state;
      editor.view.dispatch(tr.delete(from, from + 1));
      return true;
    }
  }

  return deleteSelection(doc, editor.view);
}

function isChangeRequestBoundaryNode(node: ProsemirrorNode | null | undefined): boolean {
  return node?.type.name === "changeRequest" || node?.type.name === "changeRequestPart";
}

function wouldCrossChangeRequestBoundary($pos: ResolvedPos, direction: "left" | "right"): boolean {
  const isLeft = direction === "left";
  const isAtBoundary = isLeft
    ? $pos.parentOffset === 0
    : $pos.parentOffset === $pos.parent.content.size;

  if (!isAtBoundary) {
    return false;
  }

  const { depth: maxDepth } = $pos;
  for (let depth = maxDepth; depth >= 0; depth -= 1) {
    const parent = $pos.node(depth);
    const index = isLeft ? $pos.index(depth) : $pos.indexAfter(depth);
    const isAtParentEdge = isLeft ? index === 0 : index === parent.childCount;
    if (isAtParentEdge) {
      continue;
    }

    const before = parent.maybeChild(index - 1);
    const after = parent.maybeChild(index);
    return isChangeRequestBoundaryNode(before) || isChangeRequestBoundaryNode(after);
  }

  return false;
}

function moveToSiblingSelection(direction: "left" | "right", pos: number, editor: Editor): boolean {
  const side = direction === "left" ? -1 : 1;
  const targetPos = Math.max(0, Math.min(pos + side, editor.state.doc.content.size));
  let selection = Selection.findFrom(editor.state.doc.resolve(targetPos), side);
  if (!selection) {
    return false;
  }

  // Match ArrowRight at a block boundary: it lands one character into the next text node.
  if (direction === "right" && selection.empty && selection.$head.nodeAfter?.isText) {
    selection = TextSelection.create(editor.state.doc, selection.from + 1);
  }

  editor.view.dispatch(editor.state.tr.setSelection(selection).scrollIntoView());
  return true;
}

function handleAltArrowPress(direction: "left" | "right", doc: DocumentModel, editor: Editor) {
  if (doc.selection.isKeyboardSelecting) return false;

  const { from, to, $from } = editor.state.selection;
  if (from !== to) return false;

  const node = direction === "right" ? $from.nodeAfter : $from.nodeBefore;
  if (!nodeIsExpression(node)) return false;

  const newPos = direction === "right" ? from + node.nodeSize : from - node.nodeSize;
  editor.chain().setTextSelection({ from: newPos, to: newPos }).focus().run();
  doc.cursor.reset();
  return true;
}

function handleArrowUpDownPress(direction: "up" | "down", doc: DocumentModel, editor: Editor) {
  const { state, view } = editor;
  // Allow atom NodeSelection (HR, images, etc.) — ProseMirror's default may land in adjacent empty nodes.
  const isAtomNodeSelection =
    isNodeSelection(state.selection) && state.selection.node.isAtom && state.selection.node.isBlock;

  if (!state.selection.empty && !isAtomNodeSelection) {
    return false;
  }

  if (state.selection.empty && view.endOfTextblock(direction)) {
    const side = direction === "up" ? -1 : 1;
    const { $head } = state.selection;
    const nextPos = Selection.near(
      state.doc.resolve(side > 0 ? $head.after() : $head.before()),
      side,
    );
    if (nextPos.$head?.parent.type.name === "codeBlock") {
      view.dispatch(state.tr.setSelection(nextPos).scrollIntoView());
      return true;
    }
  }

  const { left, top, bottom } = view.coordsAtPos(state.selection.from);
  return doc.cursor.jumpLine(direction, { left, top: direction === "up" ? top : bottom });
}

export function handleArrowLeftRightPress(
  direction: "left" | "right",
  doc: DocumentModel,
  editor: Editor,
  selection: Selection,
) {
  if (doc.selection.isKeyboardSelecting) return false;

  if (isCellSelection(selection)) {
    const from = Math.min(...selection.ranges.map((r) => r.$from.pos));
    const to = Math.max(...selection.ranges.map((r) => r.$to.pos));
    const nextPos = direction === "right" ? to + 1 : from + 1;
    return jumpToSiblingNode(direction === "right" ? "left" : "right", nextPos, doc, editor);
  }

  const { from, to, $from, $to } = selection;
  const isRange = from !== to;

  if (isRange) {
    const node = direction === "right" ? $to.nodeBefore : $from.nodeAfter;
    if (nodeIsExpression(node)) {
      const range = doc.selection.isWrapperNodeSelected(node.attrs.nodeId, true);
      if (range) {
        if (direction === "right") {
          editor.commands.setTextSelection({ from: to - 1, to });
          doc.cursor.moveTo(range.node, range.to, { resetSelection: true, forceFocus: true });
        } else {
          editor.commands.setTextSelection({ from, to: from + 1 });
          doc.cursor.moveTo(range.node, range.from, { resetSelection: true, forceFocus: true });
        }
        return true;
      }
    } else {
      const borderNode = direction === "right" ? $to.nodeAfter : $from.nodeBefore;
      if (nodeIsExpression(borderNode)) {
        const pos = direction === "right" ? to : from;
        editor.chain().setTextSelection({ from: pos, to: pos }).focus().run();
        doc.selection.resetSelection(false);
        doc.cursor.reset();
        return true;
      }
    }
  }

  if (direction === "right" && $to.nodeAfter?.isText) return false;
  if (direction === "left" && $from.nodeBefore?.isText) return false;

  const nextPos = direction === "right" ? to : from;
  return jumpToSiblingNode(direction, nextPos, doc, editor);
}

export function jumpToSiblingNode(
  direction: "left" | "right",
  pos: number,
  doc: DocumentModel,
  editor: Editor,
) {
  const placeCursorToEnd = direction === "left";
  const resolvedPos = editor.state.doc.resolve(pos);
  const nextSelection = Selection.findFrom(resolvedPos, direction === "left" ? -1 : 1);

  if (!nextSelection) {
    return false;
  }

  const nodeToJump =
    direction === "left" ? nextSelection.$from.nodeBefore : nextSelection.$from.nodeAfter;

  if (!nodeToJump) {
    return false;
  }

  if (nodeIsExpression(nodeToJump)) {
    editor.commands.setTextSelection(
      direction === "left" ? { from: pos - 1, to: pos } : { from: pos, to: pos + 1 },
    );
    moveToMath(doc, nodeToJump, { placeCursorToEnd });
    return true;
  } else if (nodeToJump.type.name === "hardBreak") {
    const skipPos =
      direction === "right"
        ? nextSelection.from + nodeToJump.nodeSize
        : nextSelection.from - nodeToJump.nodeSize;
    return jumpToSiblingNode(direction, skipPos, doc, editor);
  } else if (nodeToJump.type.name === "text") {
    editor
      .chain()
      .setTextSelection({ from: nextSelection.from, to: nextSelection.from })
      .focus()
      .run();
    return true;
  }

  return false;
}

export function jumpToNearbySiblingNode(doc: DocumentModel, editor?: Editor, pos?: number) {
  const tiptap = editor ?? doc.editor.tiptap;
  if (!tiptap) return false;
  const cursorPos = pos ?? tiptap.state.selection.head;

  return (
    jumpToSiblingNode("left", cursorPos, doc, tiptap) ||
    jumpToSiblingNode("right", cursorPos, doc, tiptap)
  );
}
